import { Link, useLocation, useParams } from "react-router-dom";
import { useContent } from "../api/hooks.js";
import { ErrorState, StatePanel } from "../components/primitives.jsx";
import { RowTable } from "../components/RowTable.jsx";
import { Icon } from "../components/ui/index.js";
import { Widget, WidgetSkeleton } from "../components/Widget.jsx";
import { useFilters } from "../lib/useFilters.js";
import { filterSlug } from "../../shared/filterSlug.js";
import { fmtDate, fmtDateTime } from "../lib/format.js";
import { DataStrip, PageHeader } from "../components/PageHeader.jsx";
import { ScopeBanner } from "../components/Scope.jsx";
import { useAuth } from "../auth/AuthProvider.jsx";
import { P } from "../../shared/permissions.js";
import { TAB_FOR_CONTENT_TYPE } from "../../shared/catalog.js";

/**
 * Drill-down analytics for one content item, at /content/:type/:id.
 * Uses the same tenant and filters as the dashboards (they carry over in the URL).
 */
export default function ContentPage() {
  const { type, id } = useParams();
  const { search } = useLocation();
  const f = useFilters();
  const { can } = useAuth();
  const { data, error, isLoading, isFetching, refetch } = useContent(type, id, f.apiQuery);

  if (error?.kind === "forbidden") return <StatePanel title="No access" detail={error.message || "You do not have permission to view this content."} />;

  const c = data?.content;
  const basis = data ? `${fmtDate(data.window.prevFrom)} – ${fmtDate(data.window.prevTo)}` : "the previous period";
  const back = TAB_FOR_CONTENT_TYPE[type] ?? "overview";

  return (
    <div className="flex flex-col gap-4">
      <DataStrip data={data} isFetching={isFetching} today={f.today} />
      <PageHeader
        title={c?.title || (isLoading ? "Loading…" : `${c?.typeLabel ?? type} #${id}`)}
        subtitle={`${c?.typeLabel ?? type} · ID ${id}${c?.published ? ` · ${type === "session" ? "Starts" : "Published"} ${type === "session" ? fmtDateTime(c.published) : fmtDate(c.published)}` : ""}`}
      >
        <Link to={{ pathname: `/engagement/${back}`, search }} className="mb-2 inline-flex items-center gap-1 text-[12px] font-medium text-ink-3 transition-colors hover:text-ink">
          <Icon name="arrowLeft" className="size-3.5" />
          Back to {c?.typeLabel ?? "content"}
        </Link>
      </PageHeader>

      {data && <ScopeBanner key={data.report} page={data} onRemove={(keys) => f.update(Object.fromEntries(keys.map((k) => [k, ""])))} />}

      {error && <ErrorState what="this content's analytics" message={error.message} retryable={error.retryable !== false} onRetry={() => refetch()} />}

      <div className={`grid grid-cols-12 gap-4 transition-opacity ${isFetching && data ? "opacity-60" : ""}`}>
        {isLoading && [12, 8, 4, 6, 6].map((s, i) => <WidgetSkeleton key={i} span={s} />)}
        {data?.widgets.map((w) => (
          <Widget key={`${data.report}-${w.id}`} widget={w} page={data} basis={basis} filenameBase={`${f.filters.tenant}_${type}_${id}_${f.filters.from}_${f.filters.to}${filterSlug(data.filters)}`} onRetry={refetch} />
        ))}
        {type === "session" && can(P.users) && data && (
          <RowTable id="participants" page="webinar" title="Participants of this session" extra={{ session: id }} />
        )}
      </div>
    </div>
  );
}
